import React, { useState, useEffect } from 'react';
import axios from 'axios';
import GetCookie from '../../utils/GetCookie';

const TextractorThreadPanel = ({ 
  isOpen,
  onClose,
  onSuccess,
  onError,
  onProcessingStart,
  onProcessingEnd,
  modelId,
  modelName
}) => {
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [total, setTotal] = useState(0);

  const resetPanelState = () => {
    setSelectedThreadId(null);
    setThreads([]);
    setSearchInput('');
    setSearch('');
    setPage(1);
    setTotal(0);
  };

  // Fetch threads when panel opens or search/pagination changes
  useEffect(() => {
    if (isOpen) {
      fetchThreads();
    }
  }, [isOpen, page, size, search]);

  const fetchThreads = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        '/pceapi/textractor/list-threads-for-append',
        {
          table_id: modelId,
          page: page,
          size: size,
          search: search || null,
          filter_type: null
        },
        {
          headers: {
            Authorization: GetCookie('access_token')
          }
        }
      );

      if (response.data && response.data.status === 'success') {
        const responseData = response.data.data;
        setThreads(responseData.threads || []);
        setTotal(responseData.total_threads || 0);
      } else {
        setThreads([]);
        setTotal(0);
        if (onError) {
          onError(response.data?.message || 'Failed to fetch threads');
        }
      }
    } catch (error) {
      console.error('Error fetching threads:', error);
      setThreads([]);
      setTotal(0);
      if (onError) {
        onError(error.response?.data?.message || 'Failed to fetch threads');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleThreadSelection = (threadId) => {
    setSelectedThreadId(threadId);
  };

  const handleMigrate = async () => {
    if (!selectedThreadId) {
      if (onError) {
        onError('Please select a thread');
      }
      return;
    }

    if (onProcessingStart) {
      onProcessingStart(`Migrating CSV data to thread...`);
    }

    try {
      const threadData = threads.find(thread => thread.id === selectedThreadId);
      
      if (!threadData) {
        throw new Error('Selected thread not found');
      }

      // Step 1: Download CSV from textractor
      const downloadResponse = await axios.post(
        '/pceapi/textractor/download-model',
        { table_id: modelId },
        {
          headers: {
            Authorization: GetCookie('access_token')
          },
          responseType: 'blob'
        }
      );

      const filename = `${modelName || 'extraction_data'}.csv`;
      const file = new File([downloadResponse.data], filename, {
        type: 'text/csv'
      });

      // Step 2: Upload CSV to selected thread
      const formData = new FormData();
      formData.append('thread_id', selectedThreadId);
      formData.append('name', threadData.name || 'Untitled Thread');
      formData.append('thread_access_level', threadData.thread_access_level || 'PRIVATE');
      formData.append('tags', JSON.stringify(threadData.tags || []));
      formData.append('user_access', JSON.stringify(threadData.user_access || []));
      formData.append('csvFiles', file);

      const response = await axios.post(
        '/pceapi/threads/unstructured/update',
        formData,
        {
          headers: {
            Authorization: GetCookie('access_token'),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.status === 'success') {
        resetPanelState();
        onClose();
        if (onSuccess) {
          onSuccess(`CSV data migrated to thread successfully`);
        }
      } else {
        if (onError) {
          onError(response.data.message || 'Failed to migrate CSV to thread');
        }
      }
    } catch (error) {
      console.error('Error migrating CSV:', error);
      if (onError) {
        onError(error.response?.data?.message || error.message || 'Failed to migrate CSV to thread');
      }
    } finally {
      if (onProcessingEnd) {
        onProcessingEnd();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="position-absolute ev-message-card2 ev-card-3 ev-padding-small top-100 z-1000 px-3 pt-3 end-0 margin-top-5" 
      style={{width: 'var(--px-600)'}}
    >
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span className="ev-bold-med ev-reg-p-blue ev-fs-14">
          Migrate CSV to Data Collection
        </span>
        <button 
          type="button"
          className="btn-close" 
          onClick={() => {
            resetPanelState();
            onClose();
          }}
          aria-label="Close"
        ></button>
      </div>

      {/* Search and Pagination Row */}
      <div className="d-flex justify-content-between align-items-center margin-bottom-2">
        {/* Left side - Search */}
        <div className="col-5">
          <div className="position-relative">
            <input
              type="text"
              className="form-control search-input ms-0"
              placeholder="Search Data Collections"
              value={searchInput}
              onChange={handleSearchInputChange}
              onKeyPress={handleSearchKeyPress}
            />
            <button 
              type="button"
              className="ev-btn-lightblue-fill-round search-bar-button" 
              onClick={handleSearch}
              disabled={loading}
            >
              <i className="bi bi-search"></i>
            </button>        
          </div>
        </div>

        {/* Right side - Pagination */}
        <div className="col-7 d-flex justify-content-end align-items-center">
          <div className="d-flex align-items-center">
            <button 
              type="button"
              className="ev-btn-light-fill ev-btn-xsmall" 
              disabled={page === 1 || loading}
              onClick={() => setPage(page - 1)}
            >
              <i className="bi bi-chevron-left"></i>
            </button>

            <span className="ev-xsmall-p-dark mx-2">
              Page {page}/{Math.ceil(total / size) || 1}
            </span>

            <button 
              type="button"
              className="ev-btn-light-fill ev-btn-xsmall" 
              disabled={page >= Math.ceil(total / size) || loading}
              onClick={() => setPage(page + 1)}
            >
              <i className="bi bi-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Thread List */}
      <div className="scrollable-element vh-25 margin-bottom">
        {loading ? (
          <div className="text-center py-2">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : threads.length === 0 ? (
          <p className="ev-small-p-blue text-center mt-2">No Data Collections found</p>
        ) : (
          <div className="thread-tree ms-1">
            {threads.map((thread) => (
              <div key={thread.id} className="margin-bottom-1">
                <div className="d-flex align-items-center">
                  <div className="form-check mb-0">
                    <input
                      className="form-check-input"
                      type="radio"
                      id={`textractor-thread-${thread.id}`}
                      name="selectedThread"
                      checked={selectedThreadId === thread.id}
                      onChange={() => handleThreadSelection(thread.id)}
                    />
                    <label className="form-check-label" htmlFor={`textractor-thread-${thread.id}`}>
                      <span className="ev-small-p-blue">{thread.name}</span> &nbsp;
                      <span className={`badge ev-btn-small ${thread.thread_access_level === 'PUBLIC' ? 'bg-primary' : 'bg-danger'}`}>
                        {thread.thread_access_level || 'PRIVATE'}
                      </span>
                      <span className="badge bg-secondary ms-1 ev-btn-xsmall">
                        {thread.document_count || 0} docs
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="d-flex justify-content-end gap-2">
        <button 
          type="button"
          className="ev-btn-blue-fill-round ev-btn-small ev-bold-reg"
          onClick={handleMigrate}
          disabled={!selectedThreadId || loading}
        >
          <i className="bi bi-arrow-right-circle"></i> Migrate
        </button>

        <button 
          type="button"
          className="ev-btn-blue-outline-round ev-btn-small"
          onClick={() => {
            resetPanelState();
            onClose();
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default TextractorThreadPanel;